import React, { useEffect, useState } from 'react';
import api from '../../api/client';
import { Target, Calendar, TrendingUp, CheckCircle2, AlertTriangle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const PlanVsActual: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await api.get('/recruitment-analytics/plan-vs-actual', { params: { month } });
        setData(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [month]);

  if (loading) return <div style={{ padding: 60, textAlign: 'center' }}>Загрузка...</div>;
  if (!data) return null;

  const chartData = [
    { name: 'План', value: data.planCount },
    { name: 'Факт', value: data.actualCount },
    { name: 'Прогноз', value: data.predictedTotal }
  ];

  return (
    <div style={{ padding: '0 20px 40px 20px', fontFamily: 'Inter, sans-serif' }}>
      
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(135deg, #6a11cb, #2575fc)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', boxShadow: '0 8px 20px rgba(106,17,203,0.3)' }}>
            <Target size={24} />
          </div>
          <div>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: '#1a1a2e', margin: 0 }}>План подбора vs Факт</h1>
            <p style={{ color: '#64748b', margin: '4px 0 0 0', fontSize: 14 }}>Контроль выполнения плана найма</p>
          </div>
        </div>
        <input
          type="month"
          value={month}
          onChange={e => setMonth(e.target.value)}
          style={{ padding: '10px 16px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.6)', background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(10px)', fontSize: 14, fontWeight: 500, color: '#1a1a2e' }}
        />
      </div>

      {/* KPI Карточки */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: 'rgba(37,117,252,0.1)', color: '#2575fc' }}><Target size={22} /></div>
          <div><div style={styles.kpiLabel}>План</div><div style={styles.kpiValue}>{data.planCount}</div></div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: 'rgba(106,17,203,0.1)', color: '#6a11cb' }}><CheckCircle2 size={22} /></div>
          <div><div style={styles.kpiLabel}>Факт</div><div style={styles.kpiValue}>{data.actualCount}</div></div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: data.percentComplete >= 100 ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', color: data.percentComplete >= 100 ? '#10b981' : '#ef4444' }}><TrendingUp size={22} /></div>
          <div><div style={styles.kpiLabel}>Выполнение</div><div style={{ ...styles.kpiValue, color: data.percentComplete >= 100 ? '#10b981' : '#ef4444' }}>{data.percentComplete}%</div></div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: 'rgba(245,158,11,0.1)', color: '#f59e0b' }}><Calendar size={22} /></div>
          <div><div style={styles.kpiLabel}>Прогноз</div><div style={styles.kpiValue}>{data.predictedTotal}</div></div>
        </div>
      </div>

      {/* График */}
      <div style={{ ...styles.glassCard, marginBottom: 24 }}>
        <h3 style={styles.sectionTitle}><TrendingUp size={18} /> План / Факт / Прогноз</h3>
        <div style={{ height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="planGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6a11cb" />
                  <stop offset="100%" stopColor="#2575fc" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#64748b' }} />
              <Tooltip contentStyle={{ background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(10px)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.5)' }} />
              <Bar dataKey="value" fill="url(#planGradient)" radius={[8, 8, 0, 0]} animationDuration={1000} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Таблица открытых заявок */}
      {data.pendingRequisitions?.length > 0 && (
        <div style={styles.glassCard}>
          <h3 style={styles.sectionTitle}><Calendar size={18} /> Открытые заявки без закрытия</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={styles.th}>Позиция</th>
                  <th style={styles.th}>Требуется</th>
                  <th style={styles.th}>Закрыто</th>
                  <th style={styles.th}>Просрочено</th>
                </tr>
              </thead>
              <tbody>
                {data.pendingRequisitions.map((r: any) => (
                  <tr key={r.id} style={{ borderBottom: '1px solid rgba(0,0,0,0.03)' }}>
                    <td style={styles.td}><strong>{r.position}</strong></td>
                    <td style={styles.td}>{r.requiredCount}</td>
                    <td style={styles.td}>{r.currentHires}</td>
                    <td style={styles.td}>
                      <span style={{
                        padding: '4px 10px', borderRadius: 12, fontWeight: 600, fontSize: 13,
                        background: r.overdue ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)',
                        color: r.overdue ? '#ef4444' : '#10b981',
                        display: 'inline-flex', alignItems: 'center', gap: 4
                      }}>
                        {r.overdue ? <><AlertTriangle size={12} /> Да</> : <><CheckCircle2 size={12} /> Нет</>}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  glassCard: { background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderRadius: 24, padding: 24, border: '1px solid rgba(255,255,255,0.6)', boxShadow: '0 12px 40px rgba(31,38,135,0.06)' },
  kpiCard: { background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px)', borderRadius: 20, padding: 20, border: '1px solid rgba(255,255,255,0.6)', boxShadow: '0 8px 24px rgba(31,38,135,0.04)', display: 'flex', alignItems: 'center', gap: 16 },
  kpiIcon: { width: 48, height: 48, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  kpiLabel: { fontSize: 13, color: '#64748b', fontWeight: 600, marginBottom: 4 },
  kpiValue: { fontSize: 24, fontWeight: 800, color: '#1a1a2e' },
  sectionTitle: { fontSize: 18, fontWeight: 700, color: '#1a1a2e', margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: 8 },
  th: { textAlign: 'left', padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.05)', color: '#64748b', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 },
  td: { padding: '14px 16px', borderBottom: '1px solid rgba(0,0,0,0.03)', fontSize: 14, color: '#1a1a2e' },
};

export default PlanVsActual;
